import { NotifySuccess } from '@/helpers/core/notifications'
import {
  deleteRepairRequestSubmission,
  getRepairRequestSubmissions,
  updateRepairRequestSubmissionStatus,
} from '@/services/rc/repair-request-submission.service'
import { createCustomerRepairRequestSubmission } from '@/services/rc/repair-request-submission.service'
import type { IRepairRequestSubmissionFilters } from '@/types/rc/filters'
import type { ICustomerRepairRequestSubmissionForm, IUpdateRepairSubmissionStatusForm } from '@/types/rc/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { ElMessage } from 'element-plus'

export const useRepairRequestSubmissions = () => {
  const queryClient = useQueryClient()

  const getRepairRequestSubmissionsQuery = (filters: IRepairRequestSubmissionFilters) =>
    useQuery({
      queryKey: ['repair-request-submissions', filters],
      queryFn: async () => await getRepairRequestSubmissions(filters),
      placeholderData: (prev: any) => prev,
    })

  const deleteRepairRequestSubmissionMutation = useMutation({
    mutationFn: async (id: number) => await deleteRepairRequestSubmission(id),
    onSuccess: () => {
      NotifySuccess('Repair request submission deleted successfully.')
      queryClient.invalidateQueries({ queryKey: ['repair-request-submissions'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const updateRepairRequestSubmissionStatusMutation = useMutation({
    mutationFn: async ({
      submissionID,
      updateForm,
    }: {
      submissionID: number
      updateForm: IUpdateRepairSubmissionStatusForm
    }) => await updateRepairRequestSubmissionStatus(submissionID, updateForm),
    onSuccess: () => {
      NotifySuccess('Repair request submission updated successfully.')
      queryClient.invalidateQueries({ queryKey: ['repair-request-submissions'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  const createCustomerRepairRequestSubmissionMutation = useMutation({
    mutationFn: async (form: ICustomerRepairRequestSubmissionForm) => await createCustomerRepairRequestSubmission(form),
    onSuccess: () => {
      NotifySuccess('Repair request submission created successfully.')
      queryClient.invalidateQueries({ queryKey: ['repair-request-submissions'] })
    },
    onError: (e: any) => {
      ElMessage.error(e.message)
    },
  })

  return {
    getRepairRequestSubmissionsQuery,
    deleteRepairRequestSubmissionMutation,
    updateRepairRequestSubmissionStatusMutation,
    createCustomerRepairRequestSubmissionMutation,
  }
}
